import { useEffect, useState } from 'react';
import { Loader2, Sparkles, Send, Check } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { Badge } from '@/components/atoms/badge';
import { useGenerateReplyDraft } from '@/hooks/reviews/useReplyDraft';
import { useReplyToReview, useMarkReplyHandled } from '@/hooks/reviews/useReplyToReview';
import type { ReviewPlatform, UnifiedReviewItem } from '@/types/review';

interface AiReplyComposerProps {
  item: UnifiedReviewItem;
  /**
   * Underlying row id (no `ext_`/`rsp_`/`tst_` prefix). Disables the
   * composer when missing — e.g., row id couldn't be parsed.
   */
  rawId: string | null;
  /** When false (e.g., dispatcher role without reply rights), inputs are read-only. */
  canReply: boolean;
  /** Existing AI draft text (if any) sourced from the detail row. */
  initialDraft?: string;
}

const PLATFORMS_WITH_API: ReadonlySet<ReviewPlatform> = new Set(['GOOGLE', 'FACEBOOK']);

/**
 * Composer panel for the right drawer. Branches on item.source:
 *   - EXTERNAL on Google/Facebook  → AI draft + post via platform API
 *   - EXTERNAL on Yelp/BBB/Thumbtack → "Mark handled" only (no API write)
 *   - INTERNAL_*                   → no public reply (this composer is hidden)
 */
export function AiReplyComposer({ item, rawId, canReply, initialDraft }: AiReplyComposerProps) {
  const [draft, setDraft] = useState(initialDraft ?? '');

  // When the parent re-mounts with a new item we want the draft to reset to
  // whatever's persisted, not stay cached from the previous review.
  useEffect(() => {
    setDraft(initialDraft ?? '');
  }, [item.id, initialDraft]);

  const generateExternal = useGenerateReplyDraft();
  const postReply = useReplyToReview();
  const markHandled = useMarkReplyHandled();

  /*
   * Internal rows (private feedback / public-intent) have no platform to post
   * to, so there is nothing to compose.
   *
   * This used to offer "Regenerate AI draft", which called a stubbed endpoint
   * and toasted "AI draft pending — back-end not bound yet" at the contractor.
   * Two things wrong with that: it showed a paying customer an internal wiring
   * note, and it was incoherent regardless — the copy immediately above it says
   * public replies do not apply, so a generated draft had nowhere to go.
   *
   * Removed rather than wired up. A draft for these belongs to the recovery
   * conversation ("here is what to say when you call"), which is a different
   * feature with a different destination; offering the button before that
   * exists is a promise the screen cannot keep.
   */
  if (item.source !== 'EXTERNAL') {
    return (
      <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        Internal reviews are handled with internal notes and flag-for-escalation. There is no public
        platform to reply on.
      </div>
    );
  }

  const apiCapable = !!item.platform && PLATFORMS_WITH_API.has(item.platform);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold">AI-drafted reply</Label>
        {apiCapable ? null : (
          <Badge variant="outline" className="h-5 text-[10px]">
            Reply manually on platform
          </Badge>
        )}
      </div>

      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        readOnly={!canReply}
        placeholder={
          apiCapable
            ? 'Generate an AI draft, edit if needed, then post.'
            : 'No reply API for this platform — reply on the platform and mark handled here.'
        }
        className="min-h-[120px] text-sm"
      />

      <div className="flex flex-wrap gap-2">
        {apiCapable ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canReply || !rawId || generateExternal.isPending}
              onClick={() =>
                rawId &&
                generateExternal.mutate(
                  { externalReviewId: rawId },
                  {
                    onSuccess: (result) => {
                      setDraft(result.text);
                    },
                  },
                )
              }
            >
              {generateExternal.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              )}
              {draft ? 'Regenerate draft' : 'Generate draft'}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canReply || !rawId || !draft.trim() || postReply.isPending}
              onClick={() =>
                rawId &&
                postReply.mutate({
                  externalReviewId: rawId,
                  dto: { text: draft.trim() },
                })
              }
            >
              {postReply.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="mr-1.5 h-3.5 w-3.5" />
              )}
              Post reply
            </Button>
          </>
        ) : null}

        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={!canReply || !rawId || markHandled.isPending}
          onClick={() => rawId && markHandled.mutate({ externalReviewId: rawId })}
        >
          {markHandled.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="mr-1.5 h-3.5 w-3.5" />
          )}
          Mark handled
        </Button>
      </div>
    </div>
  );
}
